
import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/ProductCard";
import { productGroups } from "@/data/products";
import { Link } from "react-router-dom";
import { Phone, FileText, MapPin, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const Index = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  // Add state for the secret pricing access
  const [logoClicks, setLogoClicks] = useState(0);

  // Access check is now handled by the AccessCheck wrapper component

  // Filter to show main categories with updated order:
  // Meeting Room Solutions should be where Audio Products was
  // Converters/Scalers should be after Meeting Room Solutions
  // HDBaseT swapped with Audio Products
  // Add HDMI Switchers category
  const mainGroups = [
    ...productGroups.filter(group => 
      group.id === "avoip" || 
      group.id === "reax-control"
    ),
    // Meeting Room Solutions moved to where Audio Products was
    ...productGroups.filter(group => group.id === "meeting-room"),
    // Converters/Scalers placed after Meeting Room Solutions
    ...productGroups.filter(group => group.id === "converters-scalers"),
    // Audio Products moved to where HDBaseT was
    ...productGroups.filter(group => group.id === "audio-products"),
    // HDMI Switchers category added
    ...productGroups.filter(group => group.id === "hdmi-switchers"),
    // IPBaseT remains in the same position
    ...productGroups.filter(group => group.id === "ipbaset"),
    // HDBaseT moved to the end where Audio Products was
    ...productGroups.filter(group => group.id === "hdbaset")
  ];

  const threeLogos = [
    "/lovable-uploads/06fe1782-824d-4c1d-9254-aab5065810bf.png", // Dante
    "/lovable-uploads/dc50d6e6-ebe6-4761-a9a7-4221a9044168.png", // AURACAST
    "/lovable-uploads/67aa6f30-ffde-42a8-b227-d61c7af63ddc.png"  // AES
  ];

  // Helper function to get the correct link for each group
  const getGroupLink = (groupId: string) => {
    switch (groupId) {
      case "avoip":
        return "/avoip-options";
      case "reax-control":
        return "/reax-options";
      case "audio-products":
        return "/audio-options";
      case "meeting-room":
        return "/group/meeting-room";
      default:
        return `/group/${groupId}`;
    }
  };

  const handleCallUSA = () => {
    window.location.href = "tel:+17325915800";
  };

  const handleCallNonUSA = () => {
    window.location.href = "tel:+447870198805";
  };

  // Handle logo click to access secret pricing
  const handleLogoClick = () => {
    const newClickCount = logoClicks + 1;
    setLogoClicks(newClickCount);
    
    // After 5 clicks, redirect to the secret pricing page
    if (newClickCount >= 5) {
      // Reset click counter
      setLogoClicks(0);
      // Navigate using the navigate function instead of directly changing window.location
      navigate('/admin-pricing');
    }
  };

  return (
    <div className="relative w-full">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="container mx-auto px-4 py-16">
        {/* Clickable Aurora Logo for secret access */}
        <div className="flex justify-center mb-8">
          <img 
            src="/lovable-uploads/29f5a874-3103-490e-ae29-e107bf621df9.png" 
            alt="Company Logo" 
            className="w-48 h-auto cursor-pointer"
            onClick={handleLogoClick}
          />
        </div>
        <div className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold tracking-tight">
            {t('product.categories')}
          </h1>
          <p className="text-lg text-muted-foreground">
            {t('explore.solutions')}
          </p>
          
          {/* Buttons Container */}
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-4">
            {/* Request Pricing Button */}
            <Link to="/request-pricing">
              <Button className="px-6 justify-center w-full" size="lg">
                {t('request.pricing')}
              </Button>
            </Link>
            
            {/* Call Us Button with Dialog */}
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" className="px-6 justify-center sm:w-auto" size="lg">
                  <Phone className="mr-2 h-5 w-5" />
                  {t('call.us')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('select.location')}</DialogTitle>
                  <DialogDescription>
                    {t('location.description')}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4 py-4">
                  <Button onClick={handleCallUSA} className="justify-center">
                    {t('usa')} (+1 732-591-5800)
                  </Button>
                  <Button onClick={handleCallNonUSA} className="justify-center">
                    {t('non.usa')} (+44 7870 198805)
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Where To Buy Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="px-6 justify-center w-full sm:w-auto" size="lg">
                  <MapPin className="mr-2 h-5 w-5" />
                  Where To Buy
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-background">
                {["America", "Europe", "Asia", "Africa", "Australia", "South America"].map((region) => (
                  <DropdownMenuItem
                    key={region}
                    className="cursor-pointer"
                    onClick={() => navigate(`/where-to-buy?region=${encodeURIComponent(region)}`)}
                  >
                    {region}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Privacy Policy Link */}
            <Link to="/privacy">
              <Button variant="outline" className="px-6 justify-center w-full sm:w-auto" size="lg">
                <FileText className="mr-2 h-5 w-5" />
                {t('privacy.policy')}
              </Button>
            </Link>
          </div>
        </div>
        
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {mainGroups.map((group) => (
            <div key={group.id} className="h-[400px] w-full animate-fade-up">
              <div className="h-full w-full bg-white rounded-md shadow hover:shadow-md transition-shadow">
                <Link 
                  to={getGroupLink(group.id)}
                  className="flex flex-col h-full p-6"
                >
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-semibold tracking-tight text-foreground">
                      {t(group.name)}
                    </h3>
                  </div>
                  
                  <div className="flex-grow flex flex-col items-center justify-center">
                    <div className="relative aspect-square w-full max-w-[200px] overflow-hidden rounded-lg bg-background/50 mb-4">
                      <img
                        src={group.image}
                        alt={t(group.name)}
                        className="h-full w-full object-scale-down p-4"
                        loading="lazy"
                        onError={(e) => {
                          console.error(`Failed to load image: ${group.image}`);
                          e.currentTarget.src = "/placeholder.svg";
                        }}
                      />
                    </div>
                    
                    {/* Aurora Logo */}
                    <div className="w-24 h-auto flex items-center justify-center mb-2">
                      <img
                        src="/lovable-uploads/29f5a874-3103-490e-ae29-e107bf621df9.png"
                        alt="Aurora Logo"
                        className="w-full h-auto object-contain"
                      />
                    </div>

                    {/* Three logos row for AV over IP only */}
                    {group.id === "avoip" && (
                      <div className="flex items-center justify-center gap-3 w-full max-w-[200px] h-10">
                        {/* Dante logo - larger and positioned left */}
                        <div className="w-14 h-10 flex items-center justify-start">
                          <img
                            src={threeLogos[0]}
                            alt="Dante Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        {/* AURACAST logo - larger and centered */}
                        <div className="w-14 h-10 flex items-center justify-center">
                          <img
                            src={threeLogos[1]}
                            alt="AURACAST Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        {/* AES logo - smaller and positioned right */}
                        <div className="w-10 h-8 flex items-center justify-end">
                          <img
                            src={threeLogos[2]}
                            alt="AES Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>
                    )}

                    {/* Two logos row for Meeting Room Solutions - exact spacing from AV over IP without middle logo */}
                    {group.id === "meeting-room" && (
                      <div className="flex items-center justify-between gap-6 w-full max-w-[200px] h-10">
                        {/* Dante logo - larger and positioned left */}
                        <div className="w-14 h-10 flex items-center justify-start">
                          <img
                            src={threeLogos[0]}
                            alt="Dante Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        {/* AES logo - smaller and positioned right */}
                        <div className="w-10 h-8 flex items-center justify-end">
                          <img
                            src={threeLogos[2]}
                            alt="AES Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>
                    )}

                    {/* Two logos row for Audio Products - exact spacing from AVOIPOptions without middle logo */}
                    {group.id === "audio-products" && (
                      <div className="flex items-center justify-between gap-6 w-full max-w-[200px] h-10">
                        {/* Dante logo - larger and positioned left */}
                        <div className="w-14 h-10 flex items-center justify-start">
                          <img
                            src={threeLogos[0]}
                            alt="Dante Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        {/* AES logo - smaller and positioned right */}
                        <div className="w-10 h-8 flex items-center justify-end">
                          <img
                            src={threeLogos[2]}
                            alt="AES Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Index;
